// VRG-643 — SEO + AEO (internal). API client for `GET|POST|PATCH /internal/seo/*`.
//
// Types are declared locally, matching `websiteAnalyticsApi.ts`: apps/platform does not
// depend on @repo/types for internal endpoints. Canonical definitions:
// `apps/api/src/features/seo/seo.types.ts`. Keep in step.
//
// No vendor credential ever reaches the browser: the API owns the SE Ranking key, Google
// impersonation and every LLM key, and this file only ever sees figures
// (`seo.guard.spec.ts` scans this app's source for the env names to keep it that way).

import { createApiCall } from './index'

export type SeoSourceStatus = 'ok' | 'not_configured' | 'disabled' | 'error'

export interface SeoSourceState {
  status: SeoSourceStatus
  message?: string
}

export interface SeoMarket {
  code: string
  name: string
  language: string
  serankingSource: string
  serpLocationId: number
  serpLocationName: string
  serpLanguageCode: string
  rankCadenceDays: number
  mode: 'full' | 'competitors_only'
  active: boolean
  notes: string | null
}

/** Stored values. The UI calls `dismissed` "Rejected" (seoReview.tsx `REVIEW_LABEL`). */
export type SeoReviewStatus = 'approved' | 'proposed' | 'dismissed'
export type SeoCompetitorKind = 'business' | 'search' | 'adjacent'

/** Why a competitor was proposed (VRG-643 follow-up). */
export interface SeoCompetitorEvidence {
  serp?: Record<
    string,
    {
      terms: number
      examples: string[]
      bestPosition: number
      hosts: string[]
      checks: number
      at: string
    }
  >
  overlap?: Record<
    string,
    { market: string; common: number; total: number | null; relevance: number | null; at: string }
  >
  note?: string
}

export interface SeoCompetitor {
  domain: string
  name: string | null
  kind: SeoCompetitorKind
  status: SeoReviewStatus
  /** seed | manual | serp | overlap */
  source: string
  /** Legacy mirror of status === 'approved'. */
  active: boolean
  notes: string | null
  evidence: SeoCompetitorEvidence | null
  statusChangedAt: string | null
  /** VRG-741 — the competitor's monitored feeds (Content → Sources rows linked to it). */
  feedCount: number
  /** VRG-741 — the automatic feed finder's last check; null = never checked. */
  feedCheck: { checkedAt: string | null; outcome: 'found' | 'none' | 'error'; note: string | null } | null
  profile: SeoCompetitorProfile
}

/** The profile vera-inc/strategy/competition.md is generated from. */
export interface SeoCompetitorProfile {
  displayName: string | null
  displayUrl: string | null
  segment: string | null
  riskRank: number | null
  hq: string | null
  founded: string | null
  funding: string | null
  scale: string | null
  pricing: string | null
  notableClients: string | null
  approxClients: string | null
  differentiation: string | null
  sources: string[]
  updatedAt: string | null
  updatedBy: string | null
}

export type SeoTrackReason = 'own-it' | 'manual' | 'rule'

export interface SeoTaxonomyTag {
  axis: 'industry' | 'solution' | 'product'
  slug: string
  label: string
}

export interface SeoTrackingSummary {
  rule: { minBuyerFit: number; minVolume: number }
  creditsPerCheck: number
  markets: Array<{
    market: string
    rule: number
    ownIt: number
    manual: number
    total: number
    cadenceDays: number
    checksPerMonth: number
    estimatedCredits: number
  }>
  totalEstimatedCredits: number
  creditCap: number
}

export interface CompetitorProposeResult {
  source: 'serp' | 'overlap'
  candidates: number
  proposed: string[]
  refreshed: string[]
  reproposed: string[]
  skippedApproved: number
  skippedDismissed: number
}

export interface SeoRunSummary {
  id: string
  job: string
  periodKey: string
  status: string
  claimedAt: string
  finishedAt: string | null
  creditsSpent: number
  /** VRG-667: 'measured' = SE Ranking balance delta; 'estimated' = per-call estimate. */
  creditsBasis: 'measured' | 'estimated'
  costUsd: number
  detail: Record<string, unknown> | null
  error: string | null
}

export interface SeoStatus {
  computedAt: string
  driver: 'live' | 'log'
  jobsEnabled: boolean
  sources: {
    seranking: SeoSourceState
    serp: SeoSourceState
    gsc: SeoSourceState
  }
  serp: { provider: string; method: string; creditsPerQuery: number }
  engines: Array<{
    id: string
    model: string
    enabled: boolean
    disabledReason: string | null
  }>
  spend: {
    month: string
    credits: number
    creditCap: number
    costUsd: number
    costCapUsd: number
  }
  /** Live balance from SE Ranking (free call), null when not configured / failed. */
  balance: {
    unitsLeft: number | null
    unitsLimit: number | null
    expiresAt: string | null
  } | null
  settings: {
    trackRule: { minBuyerFit: number; minVolume: number }
    aeoCadenceDays: number
    gscLookbackDays: number
    gapRowLimit: number
    serankingTrial: boolean
    aiSearchEnabled: boolean
    aiSearchEngines: string[]
  }
  /**
   * VRG-676 — the fixed slot rankings (every market) and AI answers run on, and each job's
   * next scheduled start (ISO; null = not scheduled on this API, disabled, or event-driven).
   */
  schedule: SeoSchedule
  markets: SeoMarket[]
  competitors: SeoCompetitor[]
  competitorCounts: Record<SeoReviewStatus, number>
  competitorRules: {
    serpMinTerms: number
    serpTopN: number
    serpChecks: number
    overlapMinCommon: number
    exclusions: string[]
  }
  runs: SeoRunSummary[]
  counts: { keywords: number; trackedByMarket: Record<string, number> }
  tracking: SeoTrackingSummary
  taxonomy: SeoTaxonomyTag[]
}

export interface SeoSchedule {
  /** The first slot: the configured weekday on or after the configured anchor date. */
  anchorDate: string
  weekday: string
  /** `HH:MM`, 24-hour, in `timeZone`. */
  localTime: string
  timeZone: string
  intervalDays: number
  aeoIntervalDays: number
  nextRuns: Partial<Record<SeoJob | 'seo-review', string | null>>
}

export interface SeoKeywordMetricView {
  month: string
  volume: number | null
  kd: number | null
  cpc: number | null
  /** Ordered oldest → newest. */
  trend: Array<{ month: string; volume: number }>
  source: string
}

export interface SeoKeywordRow {
  id: string
  keyword: string
  language: string
  translationOf: string | null
  markets: string[]
  variants: string[]
  cluster: string | null
  intent: string | null
  buyerFit: number | null
  opportunity: number | null
  priority: string | null
  targetUrl: string | null
  ownIt: boolean
  notes: string | null
  origin: string
  /** Latest metrics for the requested market (seranking preferred over csv). */
  metrics: SeoKeywordMetricView | null
  /** In the rankings tracked set for this market. */
  tracked: boolean
  status: SeoReviewStatus
  /** Who ranks for it, when a job or an import proposed it. */
  evidence: SeoKeywordEvidence | null
  trackReason: SeoTrackReason | null
  veraPosition: number | null
  veraPositionSource: 'serp' | 'gsc' | null
  estClicks: { now: number | null; at3: number | null; at1: number | null }
  adValue: { now: number | null; at1: number | null }
  trendFlag: 'rising' | 'falling' | 'verify' | null
  industries: string[]
  solutions: string[]
  products: string[]
  tagsEdited: boolean
}

export interface SeoDemandByTag {
  slug: string
  terms: number
  searches: number
  tracked: number
  avgPosition: number | null
}

export interface SeoDemandSummary {
  status: SeoReviewStatus
  buckets: Array<{ id: string; label: string; terms: number; searches: number }>
  totalTerms: number
  totalSearches: number
  fit: {
    minBuyerFit: number
    terms: number
    searches: number
    trackedTerms: number
    trackedSearches: number
    coveredShare: number | null
  }
  clicks: { now: number; at3: number; at1: number }
  adValue: { now: number; at1: number }
  ctrLabel: string
  byIndustry: SeoDemandByTag[]
  bySolution: SeoDemandByTag[]
}

export interface SeoKeywordEvidence {
  note?: string
  source?: string
  trend12m?: string
  gap?: Record<
    string,
    {
      market: string
      position: number | null
      url: string | null
      volume: number | null
      kd: number | null
      cpc?: number | null
      score?: number | null
      type?: string | null
      source: string
      at: string
    }
  >
}

export interface SeoKeywordsResponse {
  computedAt: string
  market: string
  rows: SeoKeywordRow[]
  counts: Record<SeoReviewStatus, number>
  demand: Record<SeoReviewStatus, SeoDemandSummary>
}

export interface GscTotals {
  clicks: number
  impressions: number
  ctr: number | null
  position: number | null
}

export interface GscRowView extends GscTotals {
  key: string
  previous: GscTotals | null
  /** Pages only: the query with the most clicks (impressions as tie-break). */
  topQuery?: string | null
}

export interface GscResponse {
  computedAt: string
  state: SeoSourceState
  range: { since: string; until: string }
  compare: { since: string; until: string } | null
  totals: GscTotals
  previousTotals: GscTotals | null
  /** Impressions on page totals not attributable to any query row (anonymised). */
  anonymisedImpressionShare: number | null
  daily: Array<{ date: string; clicks: number; impressions: number }>
  pages: GscRowView[]
  queries: GscRowView[]
  lastSyncedAt: string | null
}

export interface RankCell {
  domain: string
  /** Latest done check: organic position, or null = not in the top N checked. */
  position: number | null
  previous: number | null
  /** Oldest → newest, null where not ranked. */
  history: Array<number | null>
}

export interface RankRow {
  keywordId: string
  keyword: string
  cluster: string | null
  priority: string | null
  ownIt: boolean
  targetUrl: string | null
  volume: number | null
  checkedAt: string | null
  /** Vera from Search Console: impression-weighted average position, last 28 days. */
  veraGscPosition: number | null
  cells: RankCell[]
  /** A tracked competitor is in the top 10 and vera.inc is not. */
  gap: boolean
  trackReason: SeoTrackReason | null
  industries: string[]
  solutions: string[]
  products: string[]
}

export interface RankingsResponse {
  computedAt: string
  market: SeoMarket | null
  provider: { id: string; method: string }
  domains: Array<{
    domain: string
    name: string | null
    kind: 'vera' | SeoCompetitorKind
  }>
  adjacentIncluded: boolean
  adjacentAvailable: number
  depthNote: string
  rows: RankRow[]
  pendingChecks: number
  lastCheckAt: string | null
  /** Sites on page one of the latest checks that are not approved competitors. */
  discovered: Array<{ domain: string; top10: number; status: SeoReviewStatus | null }>
  /** gl= country of the most recent SERP URL — the market's location, verified. */
  verifiedCountry: string | null
}

export interface PageLeadRow {
  path: string
  clicks: number | null
  impressions: number | null
  ctr: number | null
  position: number | null
  topQuery: string | null
  visitors: number | null
  formSubmitted: number | null
  signups: number | null
  clients: number | null
}

export interface PagesToLeadsResponse {
  computedAt: string
  range: { since: string; until: string }
  sources: {
    gsc: SeoSourceState
    vercel: SeoSourceState
    posthog: SeoSourceState
  }
  rows: PageLeadRow[]
}

export interface GapRow {
  id: string
  competitor: string
  market: string
  keyword: string
  position: number | null
  url: string | null
  volume: number | null
  kd: number | null
  cpc: number | null
  traffic: number | null
  method: string
  fetchedAt: string
  /** volume × (100 − KD) / 100 — winnable demand, before intent/fit are judged. */
  score: number | null
  trackedKeywordId: string | null
  alreadyTracked: boolean
  keywordId: string | null
  keywordStatus: SeoReviewStatus | null
  industries: string[]
  solutions: string[]
  products: string[]
}

export interface GapsResponse {
  computedAt: string
  market: string
  rows: GapRow[]
  rowCap: number
  trial: boolean
  costPerPull: number
}

export interface GapPullResult {
  competitor: string
  market: string
  rows: number
  credits: number
  warning: string | null
  keywordsProposed: number
  overlap: (CompetitorProposeResult & { credits: number }) | { error: string } | null
}

export interface SerpResultsResponse {
  computedAt: string
  market: string
  keywordId: string
  keyword: string | null
  checkedAt: string | null
  provider: { id: string; method: string } | null
  results: Array<{
    position: number
    domain: string
    site: string
    url: string | null
    title: string | null
    competitor: { domain: string; kind: SeoCompetitorKind; status: SeoReviewStatus } | null
    excluded: boolean
    vera: boolean
  }>
}

export interface AeoPromptView {
  id: string
  prompt: string
  topic: string | null
  language: string
  active: boolean
  origin: string
}

export interface AeoResultView {
  promptId: string
  engine: string
  model: string | null
  runAt: string
  status: string
  samples: number
  answered: number
  citedIn: number
  mentionedIn: number
  searchedIn: number
  mentionRate: number | null
  veraCited: boolean
  veraMentioned: boolean
  veraPosition: number | null
  competitors: Array<{
    domain: string
    cited: boolean
    mentioned: boolean
    position: number | null
  }>
  citedDomains: string[]
  excerpt: string | null
  error: string | null
}

export interface AeoResponse {
  computedAt: string
  engines: SeoStatus['engines']
  prompts: AeoPromptView[]
  /** Latest result per (prompt, engine). */
  latest: AeoResultView[]
  /** Per engine, over the latest run: how often vera / each competitor was cited. */
  summary: Array<{
    engine: string
    runAt: string | null
    answered: number
    prompts: number
    samplesPerPrompt: number
    searched: number
    veraCited: number
    veraMentioned: number
    competitors: Array<{ domain: string; cited: number; mentioned: number }>
  }>
  aiSearch: Array<{
    endpoint: string
    market: string
    engine: string | null
    target: string
    brandPresence: number | null
    linkPresence: number | null
    averagePosition: number | null
    shareOfVoice: number | null
    fetchedAt: string
  }>
  aiSearchCosts: { overviewPerRequest: number; leaderboardPerRequest: number }
  costCapUsd: number
}

export interface RunJobResult {
  job: string
  claimed: boolean
  status: string
  detail?: Record<string, unknown>
  error?: string
}

export type SeoJob =
  | 'gsc-sync'
  | 'keyword-metrics'
  | 'rankings'
  | 'aeo-runner'
  | 'ai-search'
  | 'gap-remine'
  | 'master-rescore'

// ── recommendations → actions → reviews (VRG-643 follow-up, 2026-10-03) ──────────────

export type SeoRecRule =
  | 'title_rewrite'
  | 'losing'
  | 'rising_no_page'
  | 'gap_term'
  | 'cannibalisation'
  | 'aeo_change'
  | 'review_queue'
  | 'strategy_reset'

export type SeoRecStatus = 'open' | 'approved' | 'rejected' | 'snoozed' | 'resolved'
export type SeoActionStatus = 'approved' | 'in_progress' | 'shipped' | 'measured'

export interface SeoRecommendation {
  id: string
  rule: SeoRecRule
  ruleLabel: string
  market: string | null
  subject: string
  keywordId: string | null
  title: string
  evidence: Record<string, unknown>
  suggestedAction: string
  /** Ordering only: estimated monthly clicks at stake, or a fixed rule weight. */
  score: number
  status: SeoRecStatus
  snoozedUntil: string | null
  decidedBy: string | null
  decidedAt: string | null
  decisionNote: string | null
  firstSeenAt: string
  lastSeenAt: string
  resolvedAt: string | null
  actionId: string | null
}

export interface SeoAction {
  id: string
  recommendationId: string | null
  rule: SeoRecRule
  ruleLabel: string
  market: string | null
  subject: string
  keywordId: string | null
  title: string
  status: SeoActionStatus
  baseline: Record<string, unknown> | null
  measurement: Record<string, unknown> | null
  notes: string | null
  approvedBy: string | null
  approvedAt: string
  startedAt: string | null
  shippedAt: string | null
  measuredAt: string | null
  updatedBy: string | null
  updatedAt: string
}

export interface SeoRecThresholds {
  windowDays: number
  titleMinImpressions: number
  titleCtrRatio: number
  titlePositionMin: number
  titlePositionMax: number
  losingPositionDrop: number
  risingTrendPct: number
  minVolume: number
  gapMaxPosition: number
  gapMaxItems: number
  cannibalMinImpressions: number
  snoozeDays: number
}

export interface SeoRecommendationsResponse {
  items: SeoRecommendation[]
  actions: SeoAction[]
  counts: Record<SeoRecStatus, number>
  lastComputedAt: string | null
  thresholds: SeoRecThresholds
  rules: Array<{ rule: SeoRecRule; label: string }>
}

export interface SeoRecomputeResult {
  computedAt: string
  firing: number
  byRule: Record<string, number>
  inserted: number
  reopened: number
  refreshed: number
  resolved: number
  skipped: boolean
}

export interface SeoReviewMove {
  keyword: string
  market: string
  from: number | null
  to: number | null
  volume: number | null
}

export interface SeoReviewCycle {
  market: string
  period: string
  runId: string
  status: string
  claimedAt: string
  pending: number
  done: number
  failed: number
  expired: number
}

export interface SeoReviewPayload {
  version: 1
  periodKey: string
  builtAt: string
  manual: boolean
  cycles: SeoReviewCycle[]
  moved: {
    improved: SeoReviewMove[]
    declined: SeoReviewMove[]
    entered: SeoReviewMove[]
    dropped: SeoReviewMove[]
    unchanged: number
    compared: number
  }
  wins: SeoReviewMove[]
  losses: SeoReviewMove[]
  competitorOvertakes: Array<{ keyword: string; market: string; domain: string }>
  gsc: {
    since: string
    until: string
    clicks: number
    impressions: number
    position: number | null
    previous: { clicks: number; impressions: number; position: number | null }
  } | null
  top: Array<
    Pick<SeoRecommendation, 'id' | 'rule' | 'ruleLabel' | 'title' | 'suggestedAction' | 'score' | 'market'>
  >
  openCount: number
  actions: Record<string, number>
  spend: { month: string; credits: number; creditCap: number; costUsd: number; costCapUsd: number }
  recompute: SeoRecomputeResult
}

export interface SeoReviewNotified {
  email: { status: 'sent' | 'partial' | 'failed' | 'skipped'; recipients: number; sent: number; reason?: string }
  slack: { status: 'sent' | 'failed' | 'not_configured' | 'skipped'; httpStatus?: number }
  at: string
}

export interface SeoReview {
  id: string
  periodKey: string
  createdAt: string
  payload: SeoReviewPayload
  narrative: string | null
  narrativeModel: string | null
  narrativeCostUsd: number | null
  narrativeSkippedReason: string | null
  notified: SeoReviewNotified | null
}

export interface SeoReviewSummary extends Omit<SeoReview, 'payload'> {
  summary: {
    manual: boolean
    cycles: Array<{ market: string; claimedAt: string }>
    open: number
    improved: number
    declined: number
  }
}

export interface SeoReviewsResponse {
  reviews: SeoReviewSummary[]
  readiness: { ready: boolean; periodKey: string | null; cycles: SeoReviewCycle[]; reason: string | null }
  channels: { emailRecipients: number; slack: boolean; narrative: boolean }
}

// ── VRG-695 — Page titles & link previews (`GET /internal/seo/page-meta`) ──────────────

export interface PageMetaThresholds {
  titleMaxChars: number
  descriptionMinChars: number
  descriptionMaxChars: number
  imageMaxBytes: number
}

export interface PageMetaFlags {
  title: 'missing' | 'long' | null
  description: 'missing' | 'short' | 'long' | null
  image: 'missing' | 'error' | 'heavy' | null
}

export interface PageMetaRow {
  route: string
  url: string
  status: number | null
  title: string | null
  titleChars: number | null
  description: string | null
  descriptionChars: number | null
  ogTitle: string | null
  ogDescription: string | null
  ogImage: string | null
  imageBytes: number | null
  imageBytesAtLeast: boolean
  imageSizeSource: 'head' | 'get' | null
  imageContentType: string | null
  imageError: string | null
  flags: PageMetaFlags
  error: string | null
  fetchMs: number | null
}

export interface PageMetaResponse {
  baseUrl: string
  sitemapUrl: string
  allowlist: string[]
  thresholds: PageMetaThresholds
  fetchedAt: string
  cached: boolean
  expiresAt: string
  durationMs: number
  error: string | null
  warnings: string[]
  counts: {
    pages: number
    titlesFlagged: number
    descriptionsFlagged: number
    imagesFlagged: number
    rowErrors: number
  }
  rows: PageMetaRow[]
}

/** VRG-729 — an SEO pull alert (seo_alerts): a failed / missed pull, the account, the balance. */
export interface SeoAlert {
  id: string
  key: string
  kind: 'pull-failed' | 'slot-missed' | 'account' | 'low-balance'
  errorClass: string | null
  severity: 'error' | 'warning'
  status: 'open' | 'resolved'
  title: string
  summary: string
  facts: Array<{ label: string; value: string }>
  occurredAt: string | null
  openedAt: string
  lastSeenAt: string
  notifiedAt: string | null
  notifyCount: number
  resolvedAt: string | null
  resolvedBy: string | null
}

export interface SeoAlertsResponse {
  /** Whether this API evaluates alerts at all; false where SEO_JOBS_ENABLED is off. */
  active: boolean
  inactiveReason: string | null
  open: SeoAlert[]
  recentlyResolved: SeoAlert[]
}

/**
 * `createApiCall`'s errorHandler RESOLVES with the error response rather than throwing
 * (services/index.ts), so the status is checked here — otherwise a 403 body would render
 * as an empty table, and "not allowed" would read as "no data".
 */
async function call<T>(
  method: 'GET' | 'POST' | 'PATCH',
  url: string,
  what: string,
  data?: unknown,
): Promise<T> {
  const response = await createApiCall({ method, url, data: data ?? {} })
  if (response.status < 200 || response.status >= 300) {
    const error = new Error(response?.data?.message || `Failed to load ${what}`) as Error & {
      status?: number
    }
    error.status = response?.status ?? 0
    throw error
  }
  return response.data as T
}

const qs = (params: Record<string, string | undefined>): string => {
  const entries = Object.entries(params).filter(
    (e): e is [string, string] => typeof e[1] === 'string' && e[1].trim() !== '',
  )
  return entries.length ? `?${new URLSearchParams(entries).toString()}` : ''
}

export const seoApi = {
  status: (): Promise<SeoStatus> => call('GET', '/internal/seo/status', 'SEO status'),
  alerts: (): Promise<SeoAlertsResponse> => call('GET', '/internal/seo/alerts', 'SEO alerts'),
  resolveAlert: (id: string): Promise<SeoAlert> =>
    call('POST', `/internal/seo/alerts/${encodeURIComponent(id)}/resolve`, 'resolve alert'),
  keywords: (market: string): Promise<SeoKeywordsResponse> =>
    call('GET', `/internal/seo/keywords${qs({ market })}`, 'keywords'),
  gsc: (range: { since?: string; until?: string; compare?: boolean }): Promise<GscResponse> =>
    call(
      'GET',
      `/internal/seo/gsc${qs({
        since: range.since,
        until: range.until,
        compare: range.compare ? '1' : undefined,
      })}`,
      'Search Console',
    ),
  rankings: (market: string, adjacent = false): Promise<RankingsResponse> =>
    call(
      'GET',
      `/internal/seo/rankings${qs({ market, adjacent: adjacent ? '1' : undefined })}`,
      'rankings',
    ),
  serp: (market: string, keywordId: string): Promise<SerpResultsResponse> =>
    call('GET', `/internal/seo/rankings/serp${qs({ market, keywordId })}`, 'result page'),
  pages: (range: { since?: string; until?: string }): Promise<PagesToLeadsResponse> =>
    call('GET', `/internal/seo/pages${qs({ ...range })}`, 'pages to leads'),
  gaps: (market: string): Promise<GapsResponse> =>
    call('GET', `/internal/seo/gaps${qs({ market })}`, 'gap keywords'),
  aeo: (): Promise<AeoResponse> => call('GET', '/internal/seo/aeo', 'AI answers'),
  /** VRG-695. Takes no URL: the server reads its own configured site. */
  pageMeta: (refresh = false): Promise<PageMetaResponse> =>
    call('GET', `/internal/seo/page-meta${qs({ refresh: refresh ? '1' : undefined })}`, 'page meta'),

  // Writes — platform_admin only on the server.
  setOwnIt: (id: string, ownIt: boolean): Promise<{ ok: true }> =>
    call('PATCH', `/internal/seo/keywords/${encodeURIComponent(id)}`, 'keyword', { ownIt }),
  setKeywordTags: (
    id: string,
    tags: { industries?: string[]; solutions?: string[]; products?: string[] },
  ): Promise<{ ok: true }> =>
    call('PATCH', `/internal/seo/keywords/${encodeURIComponent(id)}`, 'keyword tags', tags),
  updateCompetitorProfile: (
    domain: string,
    patch: Record<string, string | number | string[] | null>,
  ): Promise<SeoCompetitor> =>
    call(
      'PATCH',
      `/internal/seo/competitors/${encodeURIComponent(domain)}/profile`,
      'competitor profile',
      patch,
    ),
  setKeywordStatus: (
    id: string,
    status: Exclude<SeoReviewStatus, 'proposed'>,
  ): Promise<{ ok: true; status: SeoReviewStatus }> =>
    call('PATCH', `/internal/seo/keywords/${encodeURIComponent(id)}`, 'keyword', { status }),
  setGapStatus: (
    id: string,
    status: Exclude<SeoReviewStatus, 'proposed'>,
  ): Promise<{ keywordId: string }> =>
    call('POST', `/internal/seo/gaps/${encodeURIComponent(id)}/status`, 'gap keyword', {
      status,
    }),
  addCompetitor: (body: {
    domain: string
    kind: SeoCompetitorKind
    note?: string | null
    name?: string | null
  }): Promise<SeoCompetitor> => call('POST', '/internal/seo/competitors', 'competitor', body),
  setCompetitorStatus: (
    domain: string,
    status: Exclude<SeoReviewStatus, 'proposed'>,
    kind?: SeoCompetitorKind,
  ): Promise<SeoCompetitor> =>
    call('PATCH', `/internal/seo/competitors/${encodeURIComponent(domain)}`, 'competitor', {
      status,
      ...(kind ? { kind } : {}),
    }),
  setCompetitorKind: (domain: string, kind: SeoCompetitorKind): Promise<SeoCompetitor> =>
    call('PATCH', `/internal/seo/competitors/${encodeURIComponent(domain)}`, 'competitor', {
      kind,
    }),
  proposeCompetitors: (): Promise<CompetitorProposeResult> =>
    call('POST', '/internal/seo/competitors/propose', 'competitor proposals'),
  runJob: (
    job: SeoJob,
    body: { market?: string; force?: boolean; limit?: number } = {},
  ): Promise<RunJobResult> => call('POST', `/internal/seo/runs/${job}`, 'job run', body),
  pullGap: (body: {
    competitor: string
    market: string
    method?: 'comparison' | 'domain_keywords'
    overlap?: boolean
  }): Promise<GapPullResult> => call('POST', '/internal/seo/gaps/pull', 'gap pull', body),
  trackGap: (id: string): Promise<{ keywordId: string }> =>
    call('POST', `/internal/seo/gaps/${encodeURIComponent(id)}/track`, 'track keyword'),
  addPrompt: (prompt: string, topic: string | null): Promise<AeoPromptView> =>
    call('POST', '/internal/seo/aeo/prompts', 'prompt', { prompt, topic }),
  setKeywordMarkets: (id: string, markets: string[]): Promise<{ ok: true }> =>
    call('PATCH', `/internal/seo/keywords/${encodeURIComponent(id)}`, 'keyword markets', {
      markets,
    }),
  recommendations: (): Promise<SeoRecommendationsResponse> =>
    call('GET', '/internal/seo/recommendations', 'recommendations'),
  recompute: (): Promise<SeoRecomputeResult> =>
    call('POST', '/internal/seo/recommendations/recompute', 'recompute'),
  decideRecommendation: (
    id: string,
    decision: 'approve' | 'reject' | 'snooze',
    body: { note?: string; days?: number } = {},
  ): Promise<{ recommendation?: SeoRecommendation; action?: SeoAction }> =>
    call(
      'POST',
      `/internal/seo/recommendations/${encodeURIComponent(id)}/${decision}`,
      'recommendation',
      body,
    ),
  setActionStatus: (id: string, status: SeoActionStatus, notes?: string): Promise<SeoAction> =>
    call('PATCH', `/internal/seo/actions/${encodeURIComponent(id)}`, 'action', { status, notes }),
  reviews: (): Promise<SeoReviewsResponse> => call('GET', '/internal/seo/reviews', 'reviews'),
  review: (id: string): Promise<SeoReview> =>
    call('GET', `/internal/seo/reviews/${encodeURIComponent(id)}`, 'review'),
  buildReview: (notify: boolean): Promise<RunJobResult> =>
    call('POST', '/internal/seo/reviews', 'review', { notify }),
  patchPrompt: (
    id: string,
    body: { prompt?: string; topic?: string | null; active?: boolean },
  ): Promise<AeoPromptView> =>
    call('PATCH', `/internal/seo/aeo/prompts/${encodeURIComponent(id)}`, 'prompt', body),
}
